import React, { createContext, ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { storageAuthTokenRemove, storageAuthTokenSave } from '../storage/storageAuthToken'
import { storageUserGet, storageUserRemove, storageUserSave } from '../storage/storageUser'
import { getProfessionalProfile } from '../redux/reducers/profile/profile.actions'
import { useAppDispatch } from '../redux/hooks'

export interface User {
  id: string
  email: string
  name: string
  birthday: Date
  genre: string
  whatsapp: string
  imagePath: string
  createdAt: Date
}

type AuthContextData = {
  signIn: (username: string, password: string) => Promise<void>
  signOut: () => void
  forgotPassword: (email: string) => Promise<void>
  user: User | null
  signWithGoogleToken: (token: string) => Promise<void>
}

type AuthProviderProps = {
  children: ReactNode
}

export const AuthContext = createContext({} as AuthContextData)

export function AuthProvider ({ children }: AuthProviderProps) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [user, setUser] = useState<User | null>(storageUserGet())

  const signIn = async (username: string, password: string): Promise<void> => {
    const { data } = await api.post('/auth', {
      username,
      password
    })
    recordUser(data)
    await verifyRestriction()
  }

  const signWithGoogleToken = async (token: string): Promise<void> => {
    const { data } = await api.post('/professional-auth-google', { token })
    recordUser(data)
    await verifyRestriction()
  }

  const signOut = () => {
    setUser(null)
    storageUserRemove()
    storageAuthTokenRemove()
    navigate('/', { replace: true })
  }

  const forgotPassword = async (email: string) => {}

  function recordUser (data: any) {
    if (data.access_token && data.refresh_token) {
      storageAuthTokenSave({
        accessToken: data.access_token,
        refreshToken: data.refresh_token
      })
    }
  }

  async function verifyRestriction () {
    const response = await dispatch(getProfessionalProfile()).unwrap()

    if (response.data) {
      setUser(response.data)
      navigate('/dashboard')
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      signIn,
      signOut,
      forgotPassword,
      signWithGoogleToken
    }}>
      {children}
    </AuthContext.Provider>
  )
}
